export type TableRowsPayload = {
  headers: string[];
  rows: string[][];
  /** The source page of each row, when the extraction recorded it. */
  rowPageNumbers?: number[];
};

export type TableRowsPayloadRef = {
  tableId: string;
  /** On a job of selected pages: the source pages whose rows to show. */
  sourcePageNumbers?: number[];
  headers: string[];
  rowCount: number;
  headerCount: number;
  rowsVersion: number;
  rowsFormat: "json" | "json.gz";
  rowsDownloadUrl: string | null;
};

type RowCacheRecord = {
  key: string;
  tableId: string;
  rowsVersion: number;
  payload: TableRowsPayload;
  updatedAt: number;
};

const DB_NAME = "constructionautomation-table-rows";
const STORE_NAME = "rows_payloads";
const DB_VERSION = 1;

const memoryCache = new Map<string, TableRowsPayload>();
const inFlightLoads = new Map<string, Promise<TableRowsPayload>>();
let dbPromise: Promise<IDBDatabase | null> | null = null;

function getCacheKey(tableId: string, rowsVersion: number): string {
  return `${tableId}:${rowsVersion}`;
}

function validateRowsPayload(
  candidate: unknown,
  fallbackHeaders: string[]
): TableRowsPayload {
  if (!candidate || typeof candidate !== "object") {
    throw new Error("Invalid row payload");
  }

  const payload = candidate as { headers?: unknown; rows?: unknown; rowPageNumbers?: unknown };
  const headers = Array.isArray(payload.headers)
    ? payload.headers.map((value) => String(value))
    : fallbackHeaders;
  const rows = Array.isArray(payload.rows)
    ? payload.rows.map((row) =>
        Array.isArray(row) ? row.map((cell) => String(cell)) : []
      )
    : null;

  if (!rows) {
    throw new Error("Invalid row payload rows");
  }

  const rowPageNumbers =
    Array.isArray(payload.rowPageNumbers) && payload.rowPageNumbers.length === rows.length
      ? payload.rowPageNumbers.map(Number)
      : undefined;
  return { headers, rows, rowPageNumbers };
}

/**
 * Keeps the rows from the ref's source pages. Payloads without row pages
 * (written before page provenance) keep every row.
 */
function rowsOnSourcePages(payload: TableRowsPayload, ref: TableRowsPayloadRef): TableRowsPayload {
  const { headers, rows, rowPageNumbers } = payload;
  if (!ref.sourcePageNumbers || !rowPageNumbers) return payload;
  const pages = new Set(ref.sourcePageNumbers);
  const kept = rows.flatMap((_, index) => (pages.has(rowPageNumbers[index]) ? [index] : []));
  return {
    headers,
    rows: kept.map((index) => rows[index]),
    rowPageNumbers: kept.map((index) => rowPageNumbers[index]),
  };
}

function getIndexedDb(): Promise<IDBDatabase | null> {
  if (typeof window === "undefined" || !("indexedDB" in window)) {
    return Promise.resolve(null);
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = window.indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
}

async function readIndexedDb(key: string): Promise<TableRowsPayload | null> {
  const db = await getIndexedDb();
  if (!db) return null;

  return await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => {
        const record = request.result as RowCacheRecord | undefined;
        resolve(record?.payload ?? null);
      };
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function writeIndexedDb(
  key: string,
  ref: TableRowsPayloadRef,
  payload: TableRowsPayload
): Promise<void> {
  const db = await getIndexedDb();
  if (!db) return;

  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.put({
        key,
        tableId: ref.tableId,
        rowsVersion: ref.rowsVersion,
        payload,
        updatedAt: Date.now(),
      } satisfies RowCacheRecord);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

function parseRowsPayloadJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error("Invalid row payload JSON", { cause: error });
  }
}

async function decodeRowsPayload(
  buffer: ArrayBuffer,
  rowsFormat: "json" | "json.gz",
  fallbackHeaders: string[]
): Promise<TableRowsPayload> {
  if (rowsFormat === "json.gz") {
    if (typeof DecompressionStream === "undefined") {
      throw new Error("Gzip decompression is not supported in this browser");
    }
    const stream = new Blob([buffer]).stream().pipeThrough(
      new DecompressionStream("gzip")
    );
    const text = await new Response(stream).text();
    return validateRowsPayload(parseRowsPayloadJson(text), fallbackHeaders);
  }

  const text = new TextDecoder().decode(buffer);
  return validateRowsPayload(parseRowsPayloadJson(text), fallbackHeaders);
}

async function fetchRowsPayload(ref: TableRowsPayloadRef): Promise<TableRowsPayload> {
  if (!ref.rowsDownloadUrl) {
    throw new Error(`Missing row payload download URL for table ${ref.tableId}`);
  }

  const response = await fetch(ref.rowsDownloadUrl);
  if (!response.ok) {
    throw new Error(
      `Row payload fetch failed for table ${ref.tableId}: ${response.status}`
    );
  }
  const buffer = await response.arrayBuffer();
  return await decodeRowsPayload(buffer, ref.rowsFormat, ref.headers);
}

export async function getTableRowsPayload(
  ref: TableRowsPayloadRef
): Promise<TableRowsPayload> {
  return rowsOnSourcePages(await loadTableRowsPayload(ref), ref);
}

async function loadTableRowsPayload(ref: TableRowsPayloadRef): Promise<TableRowsPayload> {
  const key = getCacheKey(ref.tableId, ref.rowsVersion);

  const memoryHit = memoryCache.get(key);
  if (memoryHit) return memoryHit;

  const existingLoad = inFlightLoads.get(key);
  if (existingLoad) return existingLoad;

  const loadPromise = (async () => {
    const indexedDbHit = await readIndexedDb(key);
    if (indexedDbHit) {
      memoryCache.set(key, indexedDbHit);
      return indexedDbHit;
    }

    const payload = await fetchRowsPayload(ref);
    memoryCache.set(key, payload);
    await writeIndexedDb(key, ref, payload);
    return payload;
  })();

  inFlightLoads.set(key, loadPromise);
  try {
    return await loadPromise;
  } finally {
    inFlightLoads.delete(key);
  }
}
